import React, { useEffect, useMemo, useState } from 'react'
import {
  Row,
  Divider,
  Separator,
  InfoSheetPanel,
  H3,
  Paragraph,
  BillboardButton,
  MarginBox,
  Alert,
  useOverlay,
} from '@fs/zion-ui'
import zionDebug from '@fs/zion-debug'
import { ContentDelete } from '@fs/zion-icon'
import { ProvidedMatchAttach } from '@fs/zion-record-details'
import { useQueryParams } from '@fs/zion-router'
import {
  getEventsConnectedToRelationships,
  getImage,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getPersonName,
  getPersonRelationships,
  getSexTranslatedOrOrig,
  useGenderTranslations,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import { getRecordPermissions } from '../../../../../shared/services/platform/platformService'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import getPersonFullNameFromStandards from '../../../../../shared/services/standards/names'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import useOthersOnThisRecord from '../../hooks/useOthersOnThisRecord'
import { DeleteOverlay } from '../overlays'
import FieldWrapper from '../ui/FieldWrapper'
import FieldDisplay from './components/FieldDisplay'
import RowWrapper from '../ui/RowWrapper'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import useMultiImageArksWarning from './hooks/useMultiImageArksWarning'
import MultipleImageArksWarning from './components/MultipleImageArksWarning'
import RelationshipSection from './components/RelationshipSection'
import EventsSection from './components/EventsSection'
import NamesSection from './components/NamesSection'
import AgeSection from './components/AgeSection'
import Subsection from './components/Subsection'
import BottomSheetDivider from './components/BottomSheetDivider'
import OtherFieldsSection from './components/OtherFieldsSection'
import GatedRecordDetailsLink from './components/GatedRecordDetailsLink'
import AddEventOptionsOverlay from './components/AddEventOptionsOverlay'
import useCollectionAlertBanner from '../../../../providers/collectionProvider/hooks/useCollectionAlertBanner'

const debug = zionDebug('rip:R2:PersonOverview')

function MatchAttachSection({ ark }) {
  return (
    <>
      <ProvidedMatchAttach ark={ark} />
      <Divider marginY="xs" />
    </>
  )
}

export default function PersonOverview({ handleDeletePerson }) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const deletePersonOverlay = useOverlay()
  const { viewIndexOverlay, closeAll, topMostPanel, openOverlay, infoSheetOverlay, closeOverlay } = useOverlayContext()
  const currentImage = useMemo(
    () => getImage(zionSlsState.images, zionSlsState.currentImageArk),
    [zionSlsState.images, zionSlsState.currentImageArk]
  )
  const [recordPermissions, setRecordPermissions] = useState(false)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State.selectedPersonId)
  const currentPersonRecord = getNestedOrFloatingElementById(zionSlsState.images, selectedPerson?.recordId)
  const { removeQuery } = useQueryParams()
  const { collectionId } = useCollectionContextProvider()
  const { alertBannerProps: collectionBannerProps, showBanner: collectionShowBanner } = useCollectionAlertBanner({
    collectionId,
  })
  const genderTranslations = useGenderTranslations()

  // Filter and process fields for the ui
  const nameElements = selectedPerson?.subElements?.filter((se) => se.elementType === 'NAME') || []
  const raceOrColor = getNestedSubelementsByType(selectedPerson, 'RACE_OR_COLOR')?.[0]
  const sexCode = getNestedSubelementsByType(selectedPerson, 'SEX_CODE')?.[0]
  const age = selectedPerson?.subElements?.find((se) => se.elementType === 'AGE')
  const relationshipFields = getPersonRelationships(selectedPerson, zionSlsState.images)

  const {
    showWarning,
    disableEdit,
    loading: imagesLoading,
    loadMissingImages,
    dismissWarning,
  } = useMultiImageArksWarning({
    person: selectedPerson,
    disabled: topMostPanel() !== 'viewIndex',
  })

  const othersOnThisRecord = useOthersOnThisRecord(
    currentPersonRecord,
    relationshipFields,
    zionSlsState.currentImageArk,
    thumbnails,
    r2State.selectedPersonId
  )

  const eventFields = useMemo(
    () => selectedPerson?.subElements?.filter((se) => se.elementType === 'EVENT') || [],
    [selectedPerson]
  )
  const otherFields =
    selectedPerson?.subElements?.filter(
      (se) =>
        (!se.subElements || se.elementType === 'FIELD') &&
        se.fieldType !== 'SEX_CODE' &&
        se.fieldType !== 'RACE_OR_COLOR' &&
        se.elementType !== 'AGE'
    ) || [] // All other non structured fields
  // UI Information
  const heading = getPersonName({ person: selectedPerson })
  const oneName = getPersonName({ person: selectedPerson, oneName: true })
  const instructions = t('view-index.instructions', 'Click any edit button to fix the text or highlights.')
  const h = useSlsHighlightsContext()
  const addEventOptionsOverlay = useOverlay()

  useEffect(() => {
    let isMounted = true
    async function call() {
      if (!selectedPerson?.recordId) return false
      const bool = await getRecordPermissions(currentImage.elements, selectedPerson?.recordId)
      if (isMounted) {
        setRecordPermissions(bool)
      }
      return null
    }
    call()
    return () => {
      isMounted = false
    }
  }, [currentImage, selectedPerson?.recordId])

  useEffect(() => {
    if (selectedPerson && !selectedPerson.localizedName) {
      const localizeName = async () => {
        const localizedNameInfo = await getPersonFullNameFromStandards(selectedPerson)
        zionSlsDispatch({
          type: 'SET_PERSON_LOCALIZED_NAME',
          value: {
            personId: localizedNameInfo.id,
            localizedName: localizedNameInfo.localizedName,
            partitionKey: zionSlsState.currentImageArk,
          },
        })
      }
      localizeName()
    }
  }, [selectedPerson, zionSlsDispatch, zionSlsState.currentImageArk])

  useEffect(() => {
    if (viewIndexOverlay.isOpen && topMostPanel() === 'viewIndex') {
      const elementIds = [r2State.selectedPersonId]
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark

      h.setDisplayHighlights({
        mainPartitionKey: zionSlsState.currentImageArk,
        elementIds,
      })

      if (r2State.splitScreen && splitScreenImageArk) {
        h.setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          elementIds,
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    r2State.referenceFocusedIndex,
    r2State.selectedPersonId,
    r2State.splitScreen,
    topMostPanel,
    viewIndexOverlay.isOpen,
    zionSlsState.currentImageArk,
    thumbnails,
  ])

  const userClosedPanel = () => {
    closeOverlay('viewIndex')
    removeQuery('personArk')
    removeQuery('action')
  }

  const { restrictedFields } = useCollectionContextProvider()
  const isRestrictedNameField = restrictedFields?.includes(FIELDS.NAME)
  const isRestrictedRaceField = restrictedFields?.includes(FIELDS.RACE)
  const isRestrictedSexField = restrictedFields?.includes(FIELDS.SEX)
  const isRestrictedEssentialInfo = isRestrictedNameField && isRestrictedRaceField && isRestrictedSexField
  const isRestrictedRelationshipField = restrictedFields?.includes(FIELDS.RELATIONSHIPS)
  const isRestrictedEventField = restrictedFields?.includes(FIELDS.EVENTS)
  const isRestrictedAgeField = restrictedFields?.includes(FIELDS.AGE)
  const isRestrictedOtherField = restrictedFields?.includes(FIELDS.OTHER)
  const isRestrictedAddRemovePerson = restrictedFields?.includes(FIELDS.ADD_REMOVE_PERSON)

  const eventsConnectedToRelationships = getEventsConnectedToRelationships(selectedPerson)

  const relationships = useMemo(() => {
    return getPersonRelationships(selectedPerson, zionSlsState.images, false)
  }, [selectedPerson, zionSlsState.images])

  debug('PersonOverview Render: ', {
    recordPermissions,
    eventFields,
    types: zionSlsState.types,
    otherFields,
    selectedPerson,
    nameElements,
    raceOrColor,
    age,
    sexCode,
    relationshipFields,
    nonDisplayableFields: otherFields.filter((f) => !f?.typeData?.displayable),
    selectedPerson_subElements: selectedPerson?.subElements,
    zionSlsState,
    r2State,
  })

  return (
    <InfoSheetPanel
      title={t('view-index.title', `View {name}`, { name: heading })}
      headingColor="blue"
      {...{ ...viewIndexOverlay, close: userClosedPanel }}
    >
      <MarginBox>
        <Separator size="xxs" />
        <Row wrap={false}>
          <GatedRecordDetailsLink
            recordPermissions={recordPermissions}
            selectedPersonId={selectedPerson?.id}
            heading={heading}
          />
        </Row>
        <Separator size="xs" />
        <Paragraph size="xs">{instructions}</Paragraph>
        <Separator size="xxs" />
      </MarginBox>
      <Separator size="xs" />
      {selectedPerson?.superElements && <MatchAttachSection ark={`/ark:/61903/${selectedPerson?.id}`} />}

      {/* If there are multiple image arks, show a warning before allowing editing */}

      {showWarning && (
        <MultipleImageArksWarning yesHandler={loadMissingImages} noHandler={dismissWarning} loading={imagesLoading} />
      )}

      {collectionShowBanner && (
        <Alert type="help" dismissable onDismiss={collectionBannerProps.onDismiss}>
          {collectionBannerProps.message}
        </Alert>
      )}

      {selectedPerson?.id && (
        <>
          <NamesSection
            nameElements={nameElements}
            isRestrictedEssentialInfo={isRestrictedEssentialInfo}
            diableEdit={disableEdit}
            selectedPerson={selectedPerson}
          />
          {(raceOrColor || sexCode) && (
            <>
              <Separator size="nano" />
              <FieldWrapper>
                <H3 size="H6">{`${t('view-index.more-info.title', 'More Information')}`}</H3>
              </FieldWrapper>
              <Subsection>
                <RowWrapper>
                  {raceOrColor && <FieldDisplay field={raceOrColor} value={getMostRecentFieldValue(raceOrColor)} />}
                  {sexCode && (
                    <FieldDisplay field={sexCode} value={getSexTranslatedOrOrig(sexCode, genderTranslations)} />
                  )}
                </RowWrapper>
              </Subsection>
            </>
          )}
          {infoSheetOverlay?.isBottomSheet && <BottomSheetDivider />}
          {/* Eric says they may create new structured elements, but AGE is the only one so far */}
          <AgeSection age={age} isRestrictedAgeField={isRestrictedAgeField} disableEdit={disableEdit} />
          <RelationshipSection
            relationshipFields={relationshipFields}
            isRestrictedRelationshipField={isRestrictedRelationshipField}
            disableEdit={disableEdit}
            othersOnThisRecord={othersOnThisRecord}
          />
          <EventsSection
            eventsConnectedToRelationships={eventsConnectedToRelationships}
            eventFields={eventFields}
            isRestrictedEventField={isRestrictedEventField}
            isRestrictedRelationshipField={isRestrictedRelationshipField}
            disableEdit={disableEdit}
            relationships={relationships}
            addEventOptionsOverlay={addEventOptionsOverlay}
          />
          <OtherFieldsSection
            isRestrictedOtherField={isRestrictedOtherField}
            disableEdit={disableEdit}
            openOverlay={openOverlay}
            otherFields={otherFields}
          />
          <ShowChangeHistory />
          <Row>
            <BillboardButton
              disabled={isRestrictedAddRemovePerson}
              data-testid="delete-individual-person-button"
              emphasis="low"
              color="red"
              Icon={ContentDelete}
              onClick={() => {
                deletePersonOverlay.handleClick()
              }}
            >
              {t('remove-person.delete-individual-button.text', `Delete {name}`, { name: oneName })}
            </BillboardButton>
          </Row>
          <DeleteOverlay
            overlay={deletePersonOverlay}
            headingText={t('remove-person.delete-individual-overlay.heading-text', `Delete {name}`, { name: heading })}
            handleDelete={() => {
              handleDeletePerson(r2State.selectedPersonId)
              closeAll()
            }}
          >
            <>
              {t(
                'remove-person.delete-individual-overlay.content-text',
                `Deleting {name} will remove this person and all of their content. Undo this action through this individual's Change History in the record.`,
                { name: heading }
              )}
            </>
          </DeleteOverlay>
          <AddEventOptionsOverlay addEventOptionsOverlay={addEventOptionsOverlay} />
        </>
      )}
    </InfoSheetPanel>
  )
}
